import React, { useContext, useState, useCallback, useMemo, useRef, useEffect } from "react"
import { firestore } from "../firebase/firebase"

const UnitDescriptionContext = React.createContext()

export function useUnitDescription() {
  return useContext(UnitDescriptionContext);
}

export function UnitDescriptionProvider({ children }) {

  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [error, setError] = useState('');

  // A single real-time listener is attached on first use (lazy) and shared by
  // every consumer, so pages that never ask for units pay nothing, and the
  // whole-collection read happens once — subsequent edits stream in as deltas.
  const unsubRef = useRef(null);          // active onSnapshot unsubscribe fn
  const startedRef = useRef(false);       // has the listener been attached?
  const unitsRef = useRef([]);            // latest snapshot (for promise callers)
  const firstSnapPromise = useRef(null);  // resolves on the first snapshot
  const resolveFirstSnap = useRef(null);

  const startListener = useCallback(() => {
    if (startedRef.current) return firstSnapPromise.current;
    startedRef.current = true;
    setUnitsLoading(true);
    firstSnapPromise.current = new Promise((resolve) => {
      resolveFirstSnap.current = resolve;
    });

    unsubRef.current = firestore.collection("unitDescriptions").onSnapshot(
      (querySnapshot) => {
        const unitResponse = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          'docId': doc.id,
        }));
        unitsRef.current = unitResponse;
        setUnits(unitResponse);
        setUnitsLoading(false);
        setError('');
        if (resolveFirstSnap.current) {
          resolveFirstSnap.current(unitResponse);
          resolveFirstSnap.current = null;
        }
      },
      (err) => {
        setUnitsLoading(false);
        setError(err.message);
        console.log(err.message);
        // allow a future attempt to re-attach if the listen failed
        startedRef.current = false;
        unsubRef.current = null;
        if (resolveFirstSnap.current) {
          resolveFirstSnap.current([]);
          resolveFirstSnap.current = null;
        }
      }
    );

    return firstSnapPromise.current;
  }, []);

  // detach the listener when the provider unmounts
  useEffect(() => () => { if (unsubRef.current) unsubRef.current(); }, []);

  // fire-and-forget trigger (signature unchanged); attaches the listener once
  const getUnits = useCallback(() => { startListener(); }, [startListener]);

  // resolves with the latest units once the first snapshot has arrived
  const getUnitsPromise = useCallback(async () => {
    await startListener();
    return unitsRef.current;
  }, [startListener]);

  // exception: selector reads live `units`
  const getUnit = useCallback((unitNumber) => {
    return units?.filter(unit => unit.unitNumber === unitNumber)
  }, [units])

  const value = useMemo(() => ({
    units,
    unitsLoading,
    error,
    getUnits,
    getUnitsPromise,
    getUnit,
  }), [units, unitsLoading, error, getUnits, getUnitsPromise, getUnit])

  return (
    <UnitDescriptionContext.Provider value={value}>
      {children}
    </UnitDescriptionContext.Provider>
  )
}
